import { Component, Input, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DropdownData } from 'src/app/models/dropdownData';
import { SortObject } from 'src/app/models/sortObject';
import { LovApiService } from 'src/app/services/lov-api.service';
import { ToastService } from 'src/app/services/toast.service';
import { checkForValidationErrors } from 'src/app/utils/checkForValidationErrors';
import { clearErrors } from 'src/app/utils/clearFormErrors';
import { toggleModalBlanket } from 'src/app/utils/toggleBlanket';
import { ModalActionableComponent } from '../modal-actionable/modal-actionable.component';

@Component({
  selector: 'apc-basic-crud-modal',
  templateUrl: './basic-crud-modal.component.html',
  styleUrls: ['./basic-crud-modal.component.scss'],
})
export class BasicCrudModalComponent {
  public data: DropdownData[];
  public typesDropdown: any;
  public hideDialog: boolean = false;
  public forms = new UntypedFormArray([]);
  public newForm: UntypedFormGroup;
  public editNewForm: boolean = false;
  public submitFailed = false;
  public isSaveFormOpen: boolean = false;
  @Input() public section: string;
  @Input() public type: string;
  @Input() public icon: string;
  @Input() elemsSort: SortObject[] = [
    {
      prop: 'name',
      dir: 'asc',
    },
  ];
  @ViewChild(ModalActionableComponent) modalComponent: ModalActionableComponent;

  public permissionCreate: Object = '';
  public noValueTitle: string = '(no value)';
  public rowTitle: string = '';
  public responsiblePersons = [];
  public fileRequired: boolean = false;
  public identifier: string;

  constructor(
    private toastService: ToastService,
    private lovApiService: LovApiService,
    private translateService: TranslateService
  ) {
    this.noValueTitle = this.translateService.instant('administration.sectionsManagement.noValue');
    this.identifier='';
  }

  async initData() {
    this.permissionCreate = {
      role: 'EDITOR',
    };
    /* pick form to render end */
    this.data = [];
    this.forms = new UntypedFormArray([]);
    this.data = await this.lovApiService.getDropdownValues(this.section, this.type, this.elemsSort);

    // if data exists generate all forms
    if (this.data.length > 0) {
      this.data.forEach((el) => {
        this.forms.push(this.initForm(el));
      });
      // if not generate one empty form and put in edit mode
    } else {
      this.forms.push(this.initForm());
      let form: any = this.forms.controls[0];
      form.editForm = true;
      form.isCollapsed = true;
      setTimeout(() => toggleModalBlanket(`${this.section}-${this.type}-modal-row-0`));
    }
    // on init we want all dropdowns to be closed
    this.toggleAll();
  }

  initForm(data?) {
    let form = new UntypedFormGroup({});
    form.addControl('identifier', new UntypedFormControl(data ? data.identifier : null));
    form.addControl('name', new UntypedFormControl(data ? data.name : null, [Validators.required]));
    form.addControl('code', new UntypedFormControl(data ? data.code : null));
    if (this.section === 'budget' && this.type === 'type') {
      form.addControl('allowEmptyBudgets', new UntypedFormControl(data ? data.allowEmptyBudgets : null));
      form.addControl('group1', new UntypedFormControl(data ? data.group1 : null));
      form.addControl('projectManagerRequired', new UntypedFormControl(data ? data.projectManagerRequired : null));
      form.addControl('group2', new UntypedFormControl(data ? data.group2 : null));
      form.addControl('group3', new UntypedFormControl(data ? data.group3 : null));
      form.addControl('group4', new UntypedFormControl(data ? data.group4 : null));
      form.addControl('group5', new UntypedFormControl(data ? data.group5 : null));
      form.addControl('description', new UntypedFormControl(data ? data.description : null));
    }
    if (this.section === 'order' && this.type === 'product') {
      form.addControl('category', new UntypedFormControl(data ? data.category : null));
      form.addControl('productClass', new UntypedFormControl(data ? data.productClass : null));
      form.addControl('unitOfMeasurement', new UntypedFormControl(data ? data.unitOfMeasurement : null));
      form.addControl('macroCategory', new UntypedFormControl(data ? data.macroCategory : null));
      form.addControl('description', new UntypedFormControl(data ? data.description : null));
      form.addControl('group1', new UntypedFormControl(data ? data.group1 : null));
    }
    return form;
  }

  async saveForm(form: any) {
    if (form.valid || this.submitFailed) {
      this.submitFailed = false;

      if (form.value.identifier) {
        this.lovApiService
          .updateValue(this.section, this.type, form.value)
          .then((result) => {
            form.editForm = false;
            form.patchValue(result);
            form.markAsUntouched();
            this.toastService.showSuccess('Operation success!');
            toggleModalBlanket(
              `${this.section}-${this.type}-modal-row-${this.forms.controls.findIndex((control) => control == form)}`
            );
          })
          .catch((e) => {
            clearErrors(form);
            this.submitFailed = true;
            this.toastService.showError('Operation error!');
            if (e.validationErrors.length > 0) {
              form.markAsPristine();
              checkForValidationErrors(e, form);
            }
          });
      }
      // creating new
      else {
        this.lovApiService
          .createValue(this.section, this.type, form.value)
          .then((newItem) => {
            let newForm = this.initForm(newItem);
            this.forms.controls[this.forms.controls.length - 1] = newForm;
            form.editForm = false;
            form.markAsUntouched();
            this.toastService.showSuccess('Operation success!');
            toggleModalBlanket(
              `${this.section}-${this.type}-modal-row-${this.forms.controls.findIndex((control) => control == form)}`
            );
          })
          .catch((e) => {
            clearErrors(form);
            this.submitFailed = true;
            this.toastService.showError('Operation error!');
            if (e.validationErrors.length > 0) {
              form.markAsPristine();
              checkForValidationErrors(e, form);
            }
          });
      }
    } else {
      this.toastService.showError('Operation error!');
      form.markAsPristine();
      form.markAllAsTouched();
    }
  }

  async cancelForm(item) {
    // if canceling form that exists (edit form), fetch old form data and replace that data with current data that we want to cancel
    if (item.value.identifier) {
      const index = this.forms.controls.findIndex((p) => p.value.identifier === item.value.identifier);
      this.data = await this.lovApiService.getDropdownValues(this.section, this.type, this.elemsSort);
      let oldData = this.data.find((h) => h.identifier == item.value.identifier);
      let form = this.initForm(oldData);
      this.forms.controls[index] = form;
    }
    // if canceling form that does not exist (new form), just remove it from the list
    else {
      this.forms.controls = this.forms.controls.filter(
        (el) => el.value.identifier !== null && el.value.identifier !== undefined
      );
      if (this.forms.controls.length === 0) {
        this.modalComponent.close();
      }
    }
  }

  async deleteItem(form: any) {
    try {
      await this.lovApiService.removeValue(this.section, this.type, form.value.identifier);
      this.forms.controls = this.forms.controls.filter((el) => el.value.identifier !== form.value.identifier);
      this.toastService.showSuccess('Operation success!');
    } catch {
      this.toastService.showError('Operation error!');
    }
    if (this.forms.controls.length === 0) {
      this.modalComponent.close();
    }
  }

  toggleAll() {
    this.forms.controls.forEach((item: any) => {
      item.isCollapsed = !item.isCollapsed;
    });
  }

  editFormEvent(item, event) {
    const toggleElement = `${this.section}-${this.type}-modal-row-${this.forms.controls.findIndex(
      (control) => control == item
    )}`;
    toggleModalBlanket(toggleElement);
    if (document.getElementById(toggleElement)) {
      setTimeout(() => {
        document.getElementById(toggleElement).scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
    item.editForm = event;
  }

  addNewForm() {
    // going to check if we already have some epty form
    let emptyAlreadyExists = false;
    this.forms.controls.forEach((item) => {
      if (!item.value.identifier) {
        emptyAlreadyExists = true;
      }
    });
    // add new form only if we dont have any empty form initialized
    if (!emptyAlreadyExists) {
      this.forms.push(this.initForm());
      this.forms.controls.forEach((formControl: any) => {
        // if the form is new/being created, put it to edit mode
        if (!formControl.value.identifier) {
          formControl.editForm = true;
        }
      });
      let newSectionId = `${this.section}-${this.type}-modal-row-${this.forms.controls.length - 1}`;
      setTimeout(() => toggleModalBlanket(newSectionId), 500);
      setTimeout(() => {
        document.getElementById(newSectionId).scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'end' });
      }, 200);
    }
  }

  generateRowTitle(item) {
    return item.value.identifier ? item.value.name : '';
  }

  toggleElement(elemId) {
    this.forms.controls.forEach((item: any) => {
      if (item.value.identifier === elemId) {
        item.isCollapsed = !item.isCollapsed;
      } else {
        item.isCollapsed = true;
      }
    });
  }

  openElement(elemId){
    this.forms.controls.forEach((item: any,index: number) => {
      if (item.value.identifier === elemId) {
        item.isCollapsed = false;
        setTimeout(()=>{
          if(document.getElementById(`${this.section}-${this.type}-modal-row-${index}`)){
            document.getElementById(`${this.section}-${this.type}-modal-row-${index}`).scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });
          }
        },400);
        
      } 
    });
    this.identifier='';
  }

  async openAutomatic(obj:any){
    this.identifier= obj.identifier;
    await this.initData();  
    if(this.identifier){
      this.openElement(this.identifier);
    }
  }
}
